import React from 'react';
import { Typography } from '@mui/material';
import { booleanOverlap, booleanWithin, buffer, polygon } from '@turf/turf';
import { getLat, getLng } from '../../PolygonDrawing/PolygonDrawingUtils';

/**
 * Utility to convert Google Maps path into [lat, lng] arrays
 */
function swapCoords(coords: [number, number][]) {
  return coords.map(([lat, lng]) => [lng, lat]);
}

function getFieldHolesToKeep(field, newFieldBoundaries) {
  const existingHoles = field.polygon.coordinates.slice(1);
  const fieldHolesToKeep = [];
  const newFieldPolygon = buffer(polygon([newFieldBoundaries]), 0.0001, { units: 'kilometers' });

  existingHoles.forEach((hole) => {
    const holeSwappedCoords = swapCoords(hole);
    const holePolygon = polygon([holeSwappedCoords]);

    // If the field hole is within the new boundaries and does not overlap with it, the field hole should be kept
    if (
      booleanWithin(holePolygon, newFieldPolygon) &&
      !booleanOverlap(holePolygon, newFieldPolygon)
    ) {
      fieldHolesToKeep.push(hole);
    }
  });

  return fieldHolesToKeep;
}

// Edit Field Confirmation
export const buildEditFieldConfirmation = (props, state, stopDrawing, dispatch) => {
  const { LangFile, selectedField, saveEditedFieldBoundaries } = props;
  const { polygons } = state;

  const title = LangFile.EditFieldBoundariesConfirmation.title;
  const content = (
    <div>
      <Typography variant="body1" gutterBottom>
        <strong>{LangFile.EditFieldBoundariesConfirmation.warning}: </strong>
        {LangFile.EditFieldBoundariesConfirmation.warningContent}
      </Typography>
      <Typography variant="body1">
        <strong>{LangFile.EditFieldBoundariesConfirmation.note}: </strong>
        {LangFile.EditFieldBoundariesConfirmation.noteContent}
      </Typography>
    </div>
  );

  const drawnPolygonCoords = polygons[0].path.map((coord) => [getLat(coord), getLng(coord)]);
  drawnPolygonCoords.push(drawnPolygonCoords[0]); // close polygon

  const fieldHolesToKeep = getFieldHolesToKeep(selectedField, drawnPolygonCoords);

  const newFieldBoundaries = swapCoords(drawnPolygonCoords);

  return {
    title,
    content,
    onCancel: () =>
      dispatch({
        type: 'SET_SHOW_DIALOG',
        payload: { key: 'showEditFieldBoundariesConfirmation', value: false },
      }),
    onConfirm: () => {
      saveEditedFieldBoundaries(newFieldBoundaries, fieldHolesToKeep);
      stopDrawing();
    },
  };
};

// Split Field Confirmation
export const buildSplitFieldConfirmation = (props, state, stopDrawing, dispatch) => {
  const { LangFile, selectedField, splitField } = props;
  const { polygons, splitFieldInputs } = state;

  const title = LangFile.SplitFieldConfirmation.title;
  const content = (
    <div>
      <Typography variant="body1" gutterBottom>
        <strong>{LangFile.SplitFieldConfirmation.warning}: </strong>
        {LangFile.SplitFieldConfirmation.warningContent}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{LangFile.SplitFieldConfirmation.note}: </strong>
        {LangFile.SplitFieldConfirmation.noteContent}
      </Typography>
      <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
        {/* Field A */}
        <div style={{ flex: 1 }}>
          <Typography variant="body1">
            <strong>{LangFile.SplitFieldConfirmation.fieldANameLabel}:</strong>{' '}
            {splitFieldInputs.fieldAName}
          </Typography>
          <Typography variant="body1">
            <strong>{LangFile.SplitFieldConfirmation.fieldANumberLabel}:</strong>{' '}
            {splitFieldInputs.fieldANumber}
          </Typography>
        </div>
        {/* Field B */}
        <div style={{ flex: 1 }}>
          <Typography variant="body1">
            <strong>{LangFile.SplitFieldConfirmation.fieldBNameLabel}:</strong>{' '}
            {splitFieldInputs.fieldBName}
          </Typography>
          <Typography variant="body1">
            <strong>{LangFile.SplitFieldConfirmation.fieldBNumberLabel}:</strong>{' '}
            {splitFieldInputs.fieldBNumber}
          </Typography>
        </div>
      </div>
    </div>
  );

  // Field A
  const splitFieldACoordinates = polygons[0].path.map((c) => [getLat(c), getLng(c)]);
  splitFieldACoordinates.push(splitFieldACoordinates[0]);
  const splitFieldAHolesToKeep = getFieldHolesToKeep(selectedField, splitFieldACoordinates);

  // Field B
  const splitFieldBCoordinates = polygons[1].path.map((c) => [getLat(c), getLng(c)]);
  splitFieldBCoordinates.push(splitFieldBCoordinates[0]);
  const splitFieldBHolesToKeep = getFieldHolesToKeep(selectedField, splitFieldBCoordinates);

  const newFieldA = {
    coordinates: swapCoords(splitFieldACoordinates),
    fieldHolesToKeep: splitFieldAHolesToKeep,
    name: splitFieldInputs.fieldAName,
    mef: splitFieldInputs.fieldANumber,
  };
  const newFieldB = {
    coordinates: swapCoords(splitFieldBCoordinates),
    fieldHolesToKeep: splitFieldBHolesToKeep,
    name: splitFieldInputs.fieldBName,
    mef: splitFieldInputs.fieldBNumber,
  };

  return {
    title,
    content,
    onCancel: () =>
      dispatch({
        type: 'SET_SHOW_DIALOG',
        payload: { key: 'showSplitFieldConfirmation', value: false },
      }),
    onConfirm: () => {
      splitField(newFieldA, newFieldB);
      stopDrawing();
    },
  };
};

// Merge Fields Confirmation
export const buildMergeFieldConfirmation = (props, state, stopDrawing, dispatch) => {
  const { LangFile, mergedFields, mergeFields } = props;
  const { polygons, mergedFieldInputs } = state;
  const { fieldA, fieldB } = mergedFields;

  const title = LangFile.MergeFieldsConfirmationDialog.title;
  const content = (
    <div>
      <Typography variant="body1" gutterBottom>
        <strong>{LangFile.MergeFieldsConfirmationDialog.warning}: </strong>
        {LangFile.MergeFieldsConfirmationDialog.warningContent}
      </Typography>
      <Typography variant="body1" gutterBottom>
        <strong>{LangFile.MergeFieldsConfirmationDialog.note}: </strong>
        {LangFile.MergeFieldsConfirmationDialog.noteContent}
      </Typography>
      <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
        <div style={{ flex: 1 }}>
          <Typography variant="body1">
            <strong>{LangFile.MergeFieldsConfirmationDialog.newFieldNameLabel}:</strong>{' '}
            {mergedFieldInputs.fieldName}
          </Typography>
          <Typography variant="body1">
            <strong>{LangFile.MergeFieldsConfirmationDialog.newFieldNumberLabel}:</strong>{' '}
            {mergedFieldInputs.fieldNumber}
          </Typography>
        </div>
      </div>
    </div>
  );

  const mergeFieldCoordinates = polygons[0].path.map((c) => [getLat(c), getLng(c)]);
  mergeFieldCoordinates.push(mergeFieldCoordinates[0]);

  const fieldHolesToKeep = [
    ...getFieldHolesToKeep(fieldA, mergeFieldCoordinates),
    ...getFieldHolesToKeep(fieldB, mergeFieldCoordinates),
  ];

  return {
    title,
    content,
    onCancel: () =>
      dispatch({
        type: 'SET_SHOW_DIALOG',
        payload: { key: 'showEditMergedFieldConfirmation', value: false },
      }),
    onConfirm: () => {
      mergeFields(
        [fieldA.fieldId, fieldB.fieldId],
        swapCoords(mergeFieldCoordinates),
        fieldHolesToKeep,
        mergedFieldInputs.fieldName,
        mergedFieldInputs.fieldNumber
      );
      stopDrawing();
    },
  };
};
